import { useScrollReveal } from '../../hooks/useScrollReveal';

interface FinalCTAProps {
  heading?: string;
  subheading?: string;
}

export default function FinalCTA({ heading, subheading }: FinalCTAProps) {
  const { ref, visible } = useScrollReveal<HTMLElement>();
  const title = heading ?? 'Your Home Deserves Perfect Comfort.';
  const sub = subheading ?? 'Speak with a T.S.S.A.-certified Atlantis specialist today. Free in-home consultations across the GTA — no pressure, no obligation, ever.';

  return (
    <section ref={ref} className="relative overflow-hidden min-h-[680px] flex items-center">
      {/* Cinematic background */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/96c4a0d8b7fcf4875e5828713320902e.jpg"
          alt="Expert HVAC technician"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/96 via-black/82 to-black/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-black/55" />
        <div className="absolute inset-0" style={{ background: 'radial-gradient(ellipse 60% 70% at 20% 50%, rgba(3,13,31,0.75) 0%, transparent 60%)' }} />
      </div>

      {/* Blue atmosphere */}
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse 50% 65% at 18% 50%, rgba(26,101,224,0.12) 0%, transparent 65%)' }} />
      <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(90deg, rgba(26,101,224,0.45), rgba(26,101,224,0.15) 60%, transparent)' }} />

      <div
        className={`relative w-full max-w-[1500px] mx-auto px-6 lg:px-14 py-28 lg:py-36 transition-all duration-1000 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
      >
        <div className="max-w-2xl">
          {/* Eyebrow */}
          <div className="flex items-center gap-3 mb-7">
            <div className="w-8 h-0.5 bg-luxury-gold" />
            <span className="font-caps text-[10px] tracking-[0.35em] uppercase text-white">Take the Next Step</span>
          </div>

          {/* Headline */}
          <h2 className="font-serif text-5xl lg:text-7xl xl:text-8xl text-white leading-[1.0] mb-7 font-semibold">
            {title}
          </h2>

          {/* Sub */}
          <p className="font-sans text-base lg:text-lg text-white leading-relaxed max-w-xl mb-12">
            {sub}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-14">
            <a
              href="tel:9058454009"
              className="group inline-flex items-center justify-center gap-3 bg-luxury-gold hover:bg-luxury-gold-light text-white font-caps font-bold text-xs tracking-[0.18em] uppercase rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap shadow-gold"
              style={{ padding: '1.05rem 2.5rem' }}
            >
              <div className="w-5 h-5 flex items-center justify-center group-hover:scale-110 transition-transform">
                <i className="ri-phone-fill text-sm" />
              </div>
              <span>GTA West: (905) 845-4009</span>
            </a>
            <a
              href="tel:4168913444"
              className="inline-flex items-center justify-center gap-3 border border-white/30 hover:border-luxury-gold/70 text-white hover:text-luxury-gold font-caps font-bold text-xs tracking-[0.18em] uppercase px-9 py-4 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-sm"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-phone-line text-sm" />
              </div>
              <span>GTA East: (416) 891-3444</span>
            </a>
          </div>

          {/* Trust signals */}
          <div className="flex flex-wrap gap-5">
            {[
              { icon: 'ri-shield-check-fill', label: 'T.S.S.A. Certified' },
              { icon: 'ri-gift-fill', label: 'Free Consultations' },
              { icon: 'ri-time-fill', label: '24/7 Emergency' },
              { icon: 'ri-star-fill', label: '4.9★ HomeStars' },
              { icon: 'ri-award-fill', label: 'Est. 2002' },
            ].map((item) => (
              <div key={item.label} className="flex items-center gap-2">
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${item.icon} text-luxury-gold text-sm`} />
                </div>
                <span className="font-caps text-[10px] tracking-[0.12em] uppercase text-white whitespace-nowrap">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
